import { useEffect, useState } from 'react';
import { InfoCard, Link } from '@backstage/core-components';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import {
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

interface Notification {
  id: string;
  payload: {
    title: string;
    description?: string;
    link?: string;
    severity?: string;
    topic?: string;
  };
  created: string;
  read: boolean;
}

const severityColor = (s?: string): 'default' | 'primary' | 'secondary' => {
  if (s === 'high' || s === 'critical') return 'secondary';
  if (s === 'normal' || s === 'low') return 'primary';
  return 'default';
};

const useStyles = makeStyles(theme => ({
  loading: { display: 'flex', justifyContent: 'center', padding: theme.spacing(3) },
  empty: { padding: theme.spacing(2), color: theme.palette.text.secondary, textAlign: 'center' },
  chip: { height: 18, fontSize: '0.65rem' },
  listItem: {
    borderRadius: theme.shape.borderRadius,
    paddingRight: theme.spacing(10),
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
  unread: {
    fontWeight: 600,
  },
}));

const ago = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

export const NotificationsCard = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('notifications');
        const resp = await fetchApi.fetch(
          `${baseUrl}?pageSize=5&topic=sonarqube-quality-gate&orderBy=created&orderByDirec=desc`,
        );
        if (resp.ok) {
          const data = await resp.json();
          setNotifications((data.notifications ?? data ?? []).slice(0, 5));
        }
      } catch {
        // non-fatal
      } finally {
        setLoading(false);
      }
    })();
  }, [discoveryApi, fetchApi]);

  return (
    <InfoCard title="Quality Gate Alerts" subheader="Recent SonarQube quality gate failures">
      {loading ? (
        <div className={classes.loading}><CircularProgress size={24} /></div>
      ) : null}
      {!loading && notifications.length === 0 ? (
        <Typography className={classes.empty}>
          No quality gate failures.{' '}<Link to="/notifications">View all notifications</Link>
        </Typography>
      ) : null}
      {!loading && notifications.length > 0 ? (
        <List dense disablePadding>
          {notifications.map(n => (
            <ListItem
              key={n.id}
              button
              component="a"
              href={n.payload.link ?? '/notifications'}
              className={classes.listItem}
            >
              <ListItemText
                primary={n.payload.title}
                primaryTypographyProps={!n.read ? { className: classes.unread } : undefined}
                secondary={`${n.payload.description?.slice(0, 80) ?? ''} · ${ago(n.created)}`}
              />
              <ListItemSecondaryAction>
                <Chip
                  label={n.payload.severity ?? 'info'}
                  size="small"
                  color={severityColor(n.payload.severity)}
                  className={classes.chip}
                />
              </ListItemSecondaryAction>
            </ListItem>
          ))}
          <ListItem>
            <Typography variant="body2">
              <Link to="/notifications">View all notifications →</Link>
            </Typography>
          </ListItem>
        </List>
      ) : null}
    </InfoCard>
  );
};
